import {useCallback} from 'react';
import {ROLE_OPTIONS} from 'constants/roleOptions';
import {RoleType} from 'graphql/types/AccountType';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { SiteType } from 'graphql/types/SiteType';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';

export interface FilterOptions {
    role: RoleType | 'all';
    id: string;
    name: string;
    phone: string;
    status: number;
}

interface StatusOption {
    value: number;
    label: string;
}

interface FilterAreaProps {
    filters: FilterOptions;
    setFilters: (filters: FilterOptions) => void;
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
    role: 'all',
    id: '',
    name: '',
    phone: '',
    status: 2,
};

const FilterArea: React.FC<FilterAreaProps> = ({filters, setFilters}) => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    
    // 根据 warehouse 类型过滤角色选项
    const getAvailableRoleOptions = () => {
        const allOption = { value: 'all' as const, label: t('enum.role.all') };
        
        if (currentWarehouse?.siteType === SiteType.HUB) {
            // Hub 可筛选的角色与新建 / 编辑表单（AccountFormDrawer）可选的角色保持一致
            const hubRoles: Array<RoleType> = ['HUB_OPERATOR', 'SORTING_STAFF', 'SORTER_OPERATOR', 'RETURNS_STAFF', 'PICKUP_STAFF', 'DSP_MANAGER', 'DSP_OWNER_TEAM'];
            const hubOptions = ROLE_OPTIONS.filter(option =>
                hubRoles.includes(option.value as RoleType)
            ).map(option => ({
                value: option.value,
                label: t(`enum.role.${option.value}`)
            }));
            return [allOption, ...hubOptions];
        } else if (currentWarehouse?.siteType === SiteType.WAREHOUSE) {
            // Warehouse 显示 WAREHOUSE_OPERATOR 与 WAREHOUSE_TAG_SORTER（仓库贴码分拣员），与表单可选角色一致
            const warehouseRoles: Array<RoleType> = ['WAREHOUSE_OPERATOR', 'WAREHOUSE_TAG_SORTER'];
            const warehouseOptions = ROLE_OPTIONS.filter(option =>
                warehouseRoles.includes(option.value as RoleType)
            ).map(option => ({
                value: option.value,
                label: t(`enum.role.${option.value}`)
            }));
            return [allOption, ...warehouseOptions];
        }
        
        // 默认返回所有选项
        return ROLE_OPTIONS.map(option => ({
            value: option.value,
            label: option.value === 'all' ? t('enum.role.all') : t(`enum.role.${option.value}`)
        }));
    };
    
    const roleOptions = getAvailableRoleOptions();
    
    // 使用 i18n 系统创建国际化的状态选项
    const statusOptions: StatusOption[] = [
        {value: 2, label: t('enum.activeStatus.all')},
        {value: 1, label: t('enum.activeStatus.active')},
        {value: 0, label: t('enum.activeStatus.suspended')},
    ];
    const statusOptionsForSelect = statusOptions.map(opt => ({
        value: String(opt.value),
        label: opt.label
    }));

    const handleFilterChange = useCallback((name: string, value: any) => {
        setFilters({
            ...filters,
            [name]: value
        });
    }, [filters, setFilters]);

    // 定义每列的标签数组，用于列对齐计算
    const column1Labels = [t('central.personnel.filters.role'), t('wms.account.filters.phone')];
    const column2Labels = [t('wms.account.filters.personnelId'), t('wms.account.filters.personnelStatus')];
    const column3Labels = [t('wms.account.filters.name')];

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('central.personnel.filters.role')}
                        options={roleOptions}
                        value={filters.role}
                        onChange={(value: string) => handleFilterChange('role', (value || 'all') as any)}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column1Labels)}
                        placeholder={t('central.personnel.placeholders.role') || undefined}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={t('wms.account.filters.personnelId')}
                        value={filters.id}
                        onChange={(e) => handleFilterChange('id', e.target.value)}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={t('wms.account.filters.name')}
                        value={filters.name}
                        onChange={(e) => handleFilterChange('name', e.target.value)}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
                {/* 第二行 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('wms.account.filters.phone')}
                        value={filters.phone}
                        onChange={(e) => handleFilterChange('phone', e.target.value)}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('wms.account.filters.personnelStatus')}
                        options={statusOptionsForSelect}
                        value={String(filters.status)}
                        onChange={(value: string) => handleFilterChange('status', value ? Number(value) : 2)}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column2Labels)}
                        placeholder={t('wms.account.filters.all') || undefined}
                    />
                </div>
                <div className="min-w-0"></div>
            </div>
        </div>
    );
};

export default FilterArea;